import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";



import durationIcon from "../../assets/img/icons/clock-in-duration.svg";
import removeAssignee from "../../assets/img/icons/remove_assignee_black.svg";
import tickWhite from "../../assets/img/icons/tick_2.svg";
import { RemoveButton } from "../../components/AddMultipleMemberDropDownNew/AddMultipleMemberDropDownNew";
import { PrimaryBgButton, PrimaryWhiteBgButton } from "../../styledComponents/buttons";
import { CommonFlex, CommonGrid, CommonImage, CommonText, FormAlert, InputLabel, InputWithLabelSection, PageTitle, RedStar } from "../../styledComponents/common";
import { CheckboxDiv, CheckboxTickIcon } from "../../styledComponents/settings";
import { formatDurationToHoursMinutes } from "../../utils/helper";
import TimeRangeComponent from "../Timesheet/Daily/TimeRangeComponent";
import {
  DurationContainer,
  DurationText,
} from "./CreateShift/createShiftStyles";


const getDefaultTimes = () => {
  const roundedMinsStart = Math.ceil(moment().minutes() / 5) * 5;
  const roundedMinsEnd = Math.ceil(moment().minutes() / 5) * 5;
  return {
    start: moment().minutes(roundedMinsStart + 10).format("HH:mm"),
    end: moment().minutes(roundedMinsEnd + 70).format("HH:mm"),
  };
};

const AddJobTimeRangeModal = ({
  isOpen,
  toggle,
  onConfirm,
  selectedDate,
  selectedJob,
}) => {
  const { t } = useTranslation();
  const defaults = getDefaultTimes();
  const [startTime, setStartTime] = useState(defaults.start);
  const [endTime, setEndTime] = useState(defaults.end);
  const [isAnyTimeSet, setIsAnyTimeSet] = useState(false);
  const [saveErrors, setSaveErrors] = useState({});

  // Re-sync defaults each time the modal opens so the picker and state stay in sync
  useEffect(() => {
    if (isOpen) {
      const d = getDefaultTimes();
      setStartTime(d.start);
      setEndTime(d.end);
      setIsAnyTimeSet(false);
      setSaveErrors({});
    }
  }, [isOpen]);

  const handleStartTime = (time) => {
    setStartTime(time);
    // Clear error when user changes time
    if (saveErrors["timeDifference"]) {
      setSaveErrors({});
    }
  };

  const handleEndTime = (time) => {
    setEndTime(time);
    // Clear error when user changes time
    if (saveErrors["timeDifference"]) {
      setSaveErrors({});
    }
  };

  const handleAnytimeToggle = () => {
    setIsAnyTimeSet(!isAnyTimeSet);
  };

  const validateTimeRange = () => {
    if (isAnyTimeSet) {
      return true;
    }

    if (!startTime || !endTime) {
      setSaveErrors({
        timeDifference: "Both start and end times are required",
      });
      return false;
    }

    const start = moment(startTime, "HH:mm");
    const end = moment(endTime, "HH:mm");

    if (!end.isAfter(start)) {
      setSaveErrors({
        timeDifference: "End time must be after start time",
      });
      return false;
    }

    return true;
  };

  const getJobDurationText = () => {
    if (isAnyTimeSet || !startTime || !endTime) {
      return "0h 0m";
    }

    try {
      const start = moment(startTime, "HH:mm");
      const end = moment(endTime, "HH:mm");
      const diffMs = end.diff(start);
      const diffSeconds = diffMs / 1000;
      return formatDurationToHoursMinutes(diffSeconds);
    } catch (e) {
      return "0h 0m";
    }
  };

  const handleConfirm = () => {
    if (!validateTimeRange()) {
      return;
    }

    onConfirm({
      startTime,
      endTime,
      isAnyTimeSet,
      selectedDate,
      jobId: selectedJob?.id,
    });

    // Reset modal state
    const d = getDefaultTimes();
    setStartTime(d.start);
    setEndTime(d.end);
    setIsAnyTimeSet(false);
    setSaveErrors({});
  };

  const handleClose = () => {
    // Reset modal state
    const d = getDefaultTimes();
    setStartTime(d.start);
    setEndTime(d.end);
    setIsAnyTimeSet(false);
    setSaveErrors({});
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered style={{ width: "294px" }}>
      <ModalBody style={{ padding: "16px" }}>
        <CommonFlex justifyContent="space-between" alignItems="center" margin="0 0 20px 0">
          <PageTitle margin="0">{t("set_time")}</PageTitle>
          <RemoveButton onClick={handleClose} title={t("close")}>
            <CommonImage src={removeAssignee} alt="close" size="10px" />
          </RemoveButton>
        </CommonFlex>

        {/* {selectedJob && (
          <CommonText fontSize="13px" color="#666" margin="0 0 20px 0">
            Job: <strong>{selectedJob.name}</strong>
          </CommonText>
        )} */}

        <InputWithLabelSection>
          <CommonFlex alignItems="flex-start" direction="column" gap="15px">
            {/* Time Range Section */}
            <div >
              <CommonGrid columns="47% 7% 43%" gap="5px" margin="0px">
                <InputLabel margin="0px 0 5px 0">
                  {t("start_time")}
                  {!isAnyTimeSet && <RedStar>*</RedStar>}
                </InputLabel>
                <InputLabel margin="0px 0 5px 0">-</InputLabel>
                <InputLabel margin="0px 0 5px 0">
                  {t("end_time")}
                  {!isAnyTimeSet && <RedStar>*</RedStar>}
                </InputLabel>
              </CommonGrid>

              <div
                style={{
                  pointerEvents: isAnyTimeSet ? "none" : "auto",
                  opacity: isAnyTimeSet ? 0.5 : 1,
                }}
              >
                <TimeRangeComponent
                  setStartTime={handleStartTime}
                  setEndTime={handleEndTime}
                  initialStartTime={startTime || ""}
                  initialEndTime={endTime || ""}
                  startTimeError={
                    saveErrors && saveErrors["timeDifference"]
                  }
                  endTimeError={
                    saveErrors && saveErrors["timeDifference"]
                  }
                />
              </div>

              {saveErrors && saveErrors["timeDifference"] && (
                <FormAlert style={{ marginTop: "10px" }}>
                  {saveErrors["timeDifference"]}
                </FormAlert>
              )}

              {!isAnyTimeSet && (
                <DurationContainer style={{ margin: "10px 0 0 0" }}>
                  <DurationText
                    style={{ display: "flex", alignItems: "center", gap: "5px" }}
                    color="#353A46"
                  >
                    <img
                      src={durationIcon}
                      alt="duration icon"
                      width="15px"
                    />
                    {t("shift_duration")}: {getJobDurationText()}
                  </DurationText>
                </DurationContainer>
              )}
            </div>

            {/* Anytime Toggle */}
            <CommonFlex disableResponsive alignItems="center" gap="10px">
              <CheckboxDiv
                active={isAnyTimeSet}
                onClick={handleAnytimeToggle}
                marginLeft="0"
              >
                <CheckboxTickIcon src={tickWhite} alt="" />
              </CheckboxDiv>
              <CommonText color="#353A46" fontSize="14px" fontWeight="500">
                {t("anytime_this_day")}
              </CommonText>
            </CommonFlex>
          </CommonFlex>
        </InputWithLabelSection>

        {/* Action Buttons */}
        <CommonFlex gap="10px" margin="10px 0 0 0" justifyContent="flex-end">
          <PrimaryWhiteBgButton
            onClick={handleClose}
          >
            {t("cancel")}
          </PrimaryWhiteBgButton>
          <PrimaryBgButton
            onClick={handleConfirm}
          >
            {t("confirm")}
          </PrimaryBgButton>
        </CommonFlex>
      </ModalBody>
    </Modal>
  );
};

export default AddJobTimeRangeModal;